import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Radar, Loader2, Scan, Trash2, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar as RRadar, RadarChart, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";

export default function Intelligence() {
  const qc = useQueryClient();
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands").then(r => r.data) });
  const { data: trends = [] } = useQuery({ queryKey: ["trends"], queryFn: () => api.get("/intelligence/trends").then(r => r.data) });
  const [brandId, setBrandId] = useState("");
  const [focus, setFocus] = useState("short-form video");
  const [scanning, setScanning] = useState(false);

  React.useEffect(() => {
    if (!brandId && brands.length > 0) setBrandId(brands[0].id);
  }, [brands, brandId]);

  const scan = async () => {
    if (!brandId) { toast.error("Create a brand first"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/intelligence/scan", { brand_id: brandId, focus, count: 6 });
      toast.success(`${data.created} opportunities detected`);
      qc.invalidateQueries({ queryKey: ["trends"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const del = async (id) => {
    await api.delete(`/intelligence/trends/${id}`);
    qc.invalidateQueries({ queryKey: ["trends"] });
  };

  const selected = trends[0];

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#141414] px-8 py-5 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Cultural Intelligence</div>
          <h1 className="font-display font-bold text-2xl">Trend radar — {trends.length} live opportunities</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={brandId} onValueChange={setBrandId}>
            <SelectTrigger data-testid="brand-select" className="w-56 h-10 bg-[#0a0a0a] border-[#222] rounded-sm">
              <SelectValue placeholder="Select brand" />
            </SelectTrigger>
            <SelectContent className="bg-[#101010] border-[#222] rounded-sm">
              {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="focus-input" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus area" className="h-10 w-64 bg-[#0a0a0a] border-[#222] rounded-sm focus:border-[#F5A524] focus-visible:ring-0" />
          <Button data-testid="scan-trends-btn" onClick={scan} disabled={scanning || !brandId} className="bg-[#F5A524] hover:bg-[#D98218] text-black font-medium h-10 rounded-sm">
            {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning</> : <><Scan className="w-4 h-4 mr-2" />Scan the culture</>}
          </Button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {brands.length === 0 && (
          <div className="border border-[#F5A524]/40 bg-[#F5A524]/5 p-4 rounded-sm text-sm text-[#F5A524]">
            No brands yet — create one in <a href="/app/campaigns" className="underline">Brands & Campaigns</a> to begin scanning.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Radar visualization */}
          <div className="lg:col-span-4 border border-[#171717] bg-[#0a0a0a] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Radar className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
                <div>
                  <div className="font-display font-bold text-sm">Score profile</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">{selected?.title || "No selection"}</div>
                </div>
              </div>
            </div>
            <div className="aspect-square">
              {selected ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={[
                    { m: "Velocity", v: selected.velocity },
                    { m: "Longevity", v: selected.longevity },
                    { m: "Brand fit", v: selected.brand_fit },
                    { m: "Originality", v: 100 - selected.saturation },
                    { m: "Safety", v: 100 - selected.risk },
                  ]}>
                    <PolarGrid stroke="#222" />
                    <PolarAngleAxis dataKey="m" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                    <PolarRadiusAxis stroke="#222" tick={{ fill: "#52525B", fontSize: 9 }} />
                    <RRadar name="score" dataKey="v" stroke="#F5A524" fill="#F5A524" fillOpacity={0.25} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-[#52525B] text-xs">Scan trends to see profile</div>
              )}
            </div>
            {selected && (
              <div className="mt-4 pt-4 border-t border-[#141414]">
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mb-2">Composite score</div>
                <div className="font-display font-black text-4xl text-[#F5A524]">{Math.round(selected.virality_score)}</div>
              </div>
            )}
          </div>

          {/* Trends list */}
          <div className="lg:col-span-8 space-y-3">
            {trends.map((t, i) => (
              <motion.div
                key={t.id}
                data-testid={`trend-card-${t.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="border border-[#171717] hover:border-[#2a2a2a] bg-[#0a0a0a] p-5 group"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-[#00F0FF]">{t.category}</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">· {t.region}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg mb-2">{t.title}</h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed mb-3">{t.summary}</p>
                    {t.evidence?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {t.evidence.slice(0, 3).map((e, idx) => (
                          <span key={idx} className="px-2 py-0.5 border border-[#1f1f1f] font-mono text-[9px] text-[#A1A1AA]">{e}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="w-40 shrink-0">
                    <div className="flex items-end justify-between mb-3">
                      <div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Virality</div>
                        <div className="font-display font-bold text-3xl text-[#F5A524] leading-none">{Math.round(t.virality_score)}</div>
                      </div>
                      <TrendingUp className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
                    </div>
                    <div className="space-y-1.5">
                      {[
                        { l: "velocity", v: t.velocity, c: "#F5A524" },
                        { l: "brand fit", v: t.brand_fit, c: "#17C964" },
                        { l: "longevity", v: t.longevity, c: "#00F0FF" },
                        { l: "risk", v: t.risk, c: "#F31260" },
                      ].map(m => (
                        <div key={m.l} className="flex items-center gap-2">
                          <div className="font-mono text-[9px] uppercase text-[#52525B] w-14">{m.l}</div>
                          <div className="flex-1 h-1 bg-[#141414]">
                            <div style={{ width: `${m.v}%`, background: m.c }} className="h-full" />
                          </div>
                          <div className="font-mono text-[9px] text-white w-6 text-right">{Math.round(m.v)}</div>
                        </div>
                      ))}
                    </div>
                    <button data-testid={`trend-delete-${t.id}`} onClick={() => del(t.id)} className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[9px] uppercase tracking-widest text-[#F31260] hover:underline flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Dismiss
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
            {trends.length === 0 && (
              <div className="border border-dashed border-[#222] bg-[#0a0a0a] p-16 text-center">
                <Radar className="w-8 h-8 text-[#52525B] mx-auto mb-3" />
                <div className="font-display font-bold text-lg mb-1">No opportunities detected yet.</div>
                <div className="text-sm text-[#A1A1AA]">Select a brand and press <span className="text-[#F5A524]">Scan the culture</span>.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
